// SPDX-License-Identifier: AGPL-3.0-or-later

import {EVERYONE_MENTION, EXAMPLE_CUSTOM_URL_SLUG} from '@app/features/app/config/I18nDisplayConstants';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Channels from '@app/features/channel/state/Channels';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildVanityURLTab.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import * as PermissionUtils from '@app/features/permissions/utils/PermissionUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';

const logger = new Logger('GuildVanityURLTab');
interface FormInputs {
	code: string;
}
function hasAnyChannelViewableToEveryone(guildId: string): boolean {
	const guild = Guilds.getGuild(guildId);
	if (!guild) return false;
	const channels = Channels.getGuildChannels(guildId);
	for (const channel of channels) {
		if (channel.isGuildCategory()) continue;
		const permissions = PermissionUtils.computePermissions(guildId, channel.toJSON(), null, null, false);
		if ((permissions & Permissions.VIEW_CHANNEL) === Permissions.VIEW_CHANNEL) {
			return true;
		}
	}
	return false;
}
const GuildVanityURLTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {t} = useLingui();
	const [vanityCode, setVanityCode] = useState<string>('');
	const [uses, setUses] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(true);
	const form = useForm<FormInputs>({defaultValues: {code: ''}});
	const channels = Channels.getGuildChannels(guildId);
	const guild = Guilds.getGuild(guildId);
	const forceShowDisclaimer = DeveloperOptions.forceShowVanityURLDisclaimer;
	const hasViewableChannel = useMemo(() => {
		return hasAnyChannelViewableToEveryone(guildId);
	}, [guildId, channels, guild]);
	const fetchVanityURL = useCallback(async () => {
		try {
			setIsLoading(true);
			const data = await GuildCommands.getVanityURL(guildId);
			setVanityCode(data.code || '');
			form.reset({code: data.code || ''});
			setUses(data.uses);
		} catch (err) {
			logger.error('Failed to fetch vanity URL', err);
			form.setError('code', {
				type: 'server',
				message: t`Failed to load vanity URL. Please try again.`,
			});
		} finally {
			setIsLoading(false);
		}
	}, [guildId, form]);
	useEffect(() => {
		void fetchVanityURL();
	}, [fetchVanityURL]);
	const onSubmit = async (data: FormInputs) => {
		const trimmedValue = data.code.trim();
		if (trimmedValue && !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(trimmedValue)) {
			form.setError('code', {
				message: t`Vanity URL can only contain alphanumeric characters and internal hyphens.`,
			});
			return;
		}
		await GuildCommands.updateVanityURL(guildId, trimmedValue || null);
		setVanityCode(trimmedValue);
		ToastCommands.createToast({
			type: 'success',
			children: trimmedValue ? (
				<Trans>
					Your vanity URL has been set to {RuntimeConfig.inviteEndpoint}/{trimmedValue}
				</Trans>
			) : (
				<Trans>Your vanity URL has been removed.</Trans>
			),
		});
		await fetchVanityURL();
	};
	const {handleSubmit, isSubmitting} = useFormSubmit({
		form,
		onSubmit,
		defaultErrorField: 'code',
	});
	if (isLoading) {
		return (
			<div className={styles.spinnerContainer}>
				<Spinner />
			</div>
		);
	}
	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>
					<Trans>Custom Invite URL</Trans>
				</h2>
				<p className={styles.subtitle}>
					<Trans>Set a custom invite URL for your community.</Trans>
				</p>
			</div>
			{(!hasViewableChannel || forceShowDisclaimer) && (
				<div className={styles.warning}>
					<div className={styles.warningContent}>
						<div className={styles.warningIcon}>
							<WarningIcon size={20} weight="fill" />
						</div>
						<div className={styles.warningBody}>
							<p className={styles.warningTitle}>
								<Trans>Vanity URL won't work</Trans>
							</p>
							<p className={styles.warningText}>
								<Trans>
									At least one channel must have "View Channel" permission enabled for {EVERYONE_MENTION} in order for
									the vanity URL to work. Currently, no channels are viewable to {EVERYONE_MENTION}.
								</Trans>
							</p>
						</div>
					</div>
				</div>
			)}
			<Form form={form} onSubmit={handleSubmit}>
				<div className={styles.formCard}>
					<div>
						<div className={styles.fieldLabel}>
							<Trans>Vanity URL</Trans>
						</div>
						<div className={styles.inputRow}>
							<span className={styles.inputPrefix}>{RuntimeConfig.inviteEndpoint}/</span>
							<div className={styles.inputWrapper}>
								<Input
									{...form.register('code', {
										minLength: {
											value: 2,
											message: t`Vanity URL must be at least 2 characters long.`,
										},
										maxLength: {
											value: 32,
											message: t`Vanity URL must be no more than 32 characters long.`,
										},
									})}
									error={form.formState.errors.code?.message}
									label=""
									placeholder={EXAMPLE_CUSTOM_URL_SLUG}
									minLength={2}
									maxLength={32}
								/>
							</div>
						</div>
						<p className={styles.helpText}>
							<Trans>
								Vanity URLs must be between 2 and 32 characters long and can only contain alphanumeric characters and
								internal hyphens.
							</Trans>
						</p>
					</div>
					{vanityCode && (
						<div>
							<p className={styles.usage}>
								<Trans>Current uses:</Trans> <span className={styles.usageValue}>{uses}</span>
							</p>
						</div>
					)}
					<div className={styles.actions}>
						<Button type="submit" submitting={isSubmitting}>
							<Trans>Save</Trans>
						</Button>
					</div>
				</div>
			</Form>
		</div>
	);
});

export default GuildVanityURLTab;
